import ActivityChipTableCell from 'Components/ActivityChipTableCell'
import LabelTableCell from 'Components/LabelTableCell'
import StyledTableRow from 'Components/StyledTableRow'
import { WithLoader } from 'Components/WithLoader'
import type {
  SalesPoint,
  SalesPointAddress
} from 'Services/ServiceProviderManagement/Features/SalesPoint/types'
import { ServiceProviderTypeValue } from 'Services/ServiceProviderManagement/Features/ServiceProvider/Components/ServiceProviderTypeValue'
import { SELECTED_SERVICE_PROVIDER_PARAMETER_KEY } from 'Services/ServiceProviderManagement/Features/ServiceProvider/Hooks/useServiceProvidersWithParams'
import { useGetSingleServiceProviderQuery } from 'Services/ServiceProviderManagement/Features/ServiceProvider/serviceProviderApi'
import { IS_HSL_SERVICE_ENVIRONMENT, SERVICE_PROVIDER_MANAGEMENT_ROOT_URL } from 'Util/constants'
import { datetimeFormatter, getValueFromOptions, showValueOrHyphen } from 'Util/formatters'
import {
  Chip,
  type ChipProps,
  Link,
  Paper,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer
} from '@mui/material'
import { t } from 'ttag'

const StyledTableCell = styled(LabelTableCell)`
  width: 40%;
`

interface SalesPointDetailsProps {
  salesPoint: SalesPoint
}

function SalesPointDetails({ salesPoint }: Readonly<SalesPointDetailsProps>) {
  const { data: singleServiceProvider, isLoading: isSingleServiceProviderLoading } =
    useGetSingleServiceProviderQuery(salesPoint.service_provider_id, {
      skip: !salesPoint.service_provider_id
    })

  const chipPropsMapping: Record<string, Pick<ChipProps, 'color' | 'label'>> = {
    YES: { color: 'success', label: t`Yes` },
    NO: { color: 'error', label: t`No` }
  }

  const getChipPropsForYesOrNo = (status: boolean) => {
    return chipPropsMapping[status ? 'YES' : 'NO']
  }

  const showAddress = (address: SalesPointAddress | undefined) => {
    if (!address) return '-'

    return `${showValueOrHyphen(address?.street_address)} ${showValueOrHyphen(
      address?.postal_code
    )} ${showValueOrHyphen(address?.city)}`
  }

  const usageOptions = [
    { value: 'PROD', label: t`Production` },
    { value: 'TEST', label: t`Test` }
  ]

  return (
    <TableContainer component={Paper}>
      <WithLoader loading={isSingleServiceProviderLoading}>
        <Table sx={{ td: { border: 'none' } }} aria-label={t`Details table`}>
          <TableBody>
            <StyledTableRow>
              <StyledTableCell>{t`Sales point id`}</StyledTableCell>
              <TableCell>{showValueOrHyphen(salesPoint.sales_point_id)}</TableCell>
            </StyledTableRow>
            <StyledTableRow>
              <StyledTableCell>{t`Sales point name`}</StyledTableCell>
              <TableCell>{showValueOrHyphen(salesPoint.name)}</TableCell>
            </StyledTableRow>
            <StyledTableRow>
              <StyledTableCell>{t`Service provider`}</StyledTableCell>
              <TableCell>
                {singleServiceProvider?.name ? (
                  <Link
                    href={`${SERVICE_PROVIDER_MANAGEMENT_ROOT_URL}/service-providers?${SELECTED_SERVICE_PROVIDER_PARAMETER_KEY}=${salesPoint.service_provider_id}`}
                  >
                    {singleServiceProvider.name}
                  </Link>
                ) : (
                  '-'
                )}
              </TableCell>
            </StyledTableRow>
            <StyledTableRow>
              <StyledTableCell>{t`External identifier`}</StyledTableCell>
              <TableCell>{showValueOrHyphen(salesPoint.external_identifier)}</TableCell>
            </StyledTableRow>
            <StyledTableRow>
              <StyledTableCell>{t`Service provider type`}</StyledTableCell>
              <TableCell>
                <ServiceProviderTypeValue serviceProviderType={singleServiceProvider?.type} />
              </TableCell>
            </StyledTableRow>
            <StyledTableRow>
              <StyledTableCell>{t`Start date`}</StyledTableCell>
              <TableCell>{datetimeFormatter(salesPoint.start_date)}</TableCell>
            </StyledTableRow>
            <StyledTableRow>
              <StyledTableCell>{t`Status`}</StyledTableCell>
              <ActivityChipTableCell
                active={salesPoint.active}
                activity_end_date={salesPoint.activity_end_date}
              />
            </StyledTableRow>
            <StyledTableRow>
              <StyledTableCell>{t`Activity end date`}</StyledTableCell>
              <TableCell>{datetimeFormatter(salesPoint.activity_end_date)}</TableCell>
            </StyledTableRow>
            <StyledTableRow>
              <StyledTableCell>{t`Address`}</StyledTableCell>
              <TableCell>{showAddress(salesPoint.address)}</TableCell>
            </StyledTableRow>
            <StyledTableRow>
              <StyledTableCell>{t`Usage`}</StyledTableCell>
              <TableCell>{getValueFromOptions(salesPoint.usage, usageOptions)}</TableCell>
            </StyledTableRow>
            <StyledTableRow>
              <StyledTableCell>{t`Business id`}</StyledTableCell>
              <TableCell>{showValueOrHyphen(salesPoint.business_id)}</TableCell>
            </StyledTableRow>
            <StyledTableRow>
              <StyledTableCell>{t`Sales allowed`}</StyledTableCell>
              <TableCell>
                {<Chip size="small" {...getChipPropsForYesOrNo(salesPoint.sales_allowed)} />}
              </TableCell>
            </StyledTableRow>
            <StyledTableRow>
              <StyledTableCell>{t`Sales not allowed date`}</StyledTableCell>
              <TableCell>{datetimeFormatter(salesPoint.sales_not_allowed_date)}</TableCell>
            </StyledTableRow>
            {IS_HSL_SERVICE_ENVIRONMENT && (
              <>
                <StyledTableRow>
                  <StyledTableCell>{t`S365 account id`}</StyledTableCell>
                  <TableCell>{showValueOrHyphen(salesPoint.s365t_account_id)}</TableCell>
                </StyledTableRow>
                <StyledTableRow>
                  <StyledTableCell>{t`Customer reference name`}</StyledTableCell>
                  <TableCell>{showValueOrHyphen(salesPoint.customer_reference_name)}</TableCell>
                </StyledTableRow>
                <StyledTableRow>
                  <StyledTableCell>{t`HSL sales point`}</StyledTableCell>
                  <TableCell>
                    {<Chip size="small" {...getChipPropsForYesOrNo(salesPoint.hsl_sales_point)} />}
                  </TableCell>
                </StyledTableRow>

                <StyledTableRow>
                  <StyledTableCell>{t`Billed at sales point level`}</StyledTableCell>
                  <TableCell>
                    {
                      <Chip
                        size="small"
                        {...getChipPropsForYesOrNo(salesPoint.billed_at_sales_point_level)}
                      />
                    }
                  </TableCell>
                </StyledTableRow>
                <StyledTableRow>
                  <StyledTableCell>{t`Transfer sales to financial system`}</StyledTableCell>
                  <TableCell>
                    {
                      <Chip
                        size="small"
                        {...getChipPropsForYesOrNo(salesPoint.transfer_sales_to_financial_system)}
                      />
                    }
                  </TableCell>
                </StyledTableRow>
              </>
            )}
            <StyledTableRow>
              <StyledTableCell>{t`Source`}</StyledTableCell>
              <TableCell>{showValueOrHyphen(salesPoint.source)}</TableCell>
            </StyledTableRow>
            <StyledTableRow>
              <StyledTableCell>{t`Additional information`}</StyledTableCell>
              <TableCell>{showValueOrHyphen(salesPoint.additional_info)}</TableCell>
            </StyledTableRow>
            <StyledTableRow>
              <StyledTableCell>{t`Created`}</StyledTableCell>
              <TableCell>{datetimeFormatter(new Date(salesPoint.created_timestamp))}</TableCell>
            </StyledTableRow>
            <StyledTableRow>
              <StyledTableCell>{t`Last modified`}</StyledTableCell>
              <TableCell>{datetimeFormatter(new Date(salesPoint.modified_timestamp))}</TableCell>
            </StyledTableRow>
          </TableBody>
        </Table>
      </WithLoader>
    </TableContainer>
  )
}

export default SalesPointDetails
